import { BigNumber } from 'ethers'
import { useQuery } from 'react-query'
import { Controller, Controller__factory } from '../../typechain'
import {
  Controller as Controller51,
  Controller__factory as Controller__factory51
} from '../../typechain/predy_5_1'
import { useAddresses } from '../useAddress'
import { ONE, ZERO } from '../../constants'
import { IRMParams } from '../../utils/irm'
import { useWeb3Provider } from '../useWeb3Provider'

export type SqrtPerpAssetStatus = {
  uniswapPool: string
  tickLower: number
  tickUpper: number
  numRebalance: BigNumber
  totalAmount: BigNumber
  borrowedAmount: BigNumber
  lastRebalanceTotalSquartAmount: BigNumber
  lastFee0Growth: BigNumber
  lastFee1Growth: BigNumber
  borrowPremium0Growth: BigNumber
  borrowPremium1Growth: BigNumber
  fee0Growth: BigNumber
  fee1Growth: BigNumber
  rebalancePositionUnderlying: ScaledAssetUserStatus
  rebalancePositionStable: ScaledAssetUserStatus
  rebalanceFeeGrowthUnderlying: BigNumber
  rebalanceFeeGrowthStable: BigNumber
}

export type ScaledAssetUserStatus = {
  positionAmount: BigNumber
  lastFeeGrowth: BigNumber
}

export type ScaledAssetStatus = {
  totalCompoundDeposited: BigNumber
  totalNormalDeposited: BigNumber
  totalNormalBorrowed: BigNumber
  assetScaler: BigNumber
  assetGrowth: BigNumber
  debtGrowth: BigNumber
}

export type AssetRiskParams = {
  riskRatio: BigNumber
  rangeSize: number
  rebalanceThreshold: number
}

export type AssetPool = {
  token: string
  supplyTokenAddress: string
  tokenStatus: ScaledAssetStatus
  irmParams: IRMParams
}

export type PairStatus = {
  id: BigNumber
  pairGroupId: number
  stablePool: AssetPool
  underlyingPool: AssetPool
  riskParams: AssetRiskParams
  sqrtAssetStatus: SqrtPerpAssetStatus
  isMarginZero: boolean
  isIsolatedMode: boolean
  lastUpdateTimestamp: BigNumber
}
export type Asset = PairStatus

export function useAsset(chainId: number, assetId: number) {
  const web3Provider = useWeb3Provider(chainId)
  const addresses = useAddresses(chainId)

  return useQuery<Asset>(
    ['asset', chainId, assetId],

    async () => {
      if (!web3Provider.isSuccess) throw new Error('web3provider not set')

      const { provider, account } = web3Provider.data

      if (chainId === 8453) {
        const contract = Controller__factory51.connect(
          addresses.Controller,
          provider
        )

        return await queryAssetStatus51(contract, assetId, account)
      } else {
        const contract = Controller__factory.connect(
          addresses.Controller,
          provider
        )

        return await queryAssetStatus(contract, assetId, account)
      }
    },

    {
      enabled: web3Provider.isSuccess,
      refetchInterval: 15000
    }
  )
}

async function queryAssetStatus(
  contract: Controller,
  assetId: number,
  account: string
) {
  const asset = await contract.callStatic.getLatestAssetStatus(assetId, {
    from: account
  })

  return {
    id: asset[0],
    pairGroupId: asset[1].toNumber(),
    stablePool: {
      token: asset[2][0],
      supplyTokenAddress: asset[2][1],
      tokenStatus: {
        totalCompoundDeposited: asset[2][2][0],
        totalNormalDeposited: asset[2][2][1],
        totalNormalBorrowed: asset[2][2][2],
        assetScaler: asset[2][2][3],
        assetGrowth: asset[2][2][4],
        debtGrowth: asset[2][2][5]
      },
      irmParams: {
        baseRate: asset[2][3][0],
        kinkRate: asset[2][3][1],
        slope1: asset[2][3][2],
        slope2: asset[2][3][3]
      }
    },
    underlyingPool: {
      token: asset[3][0],
      supplyTokenAddress: asset[3][1],
      tokenStatus: {
        totalCompoundDeposited: asset[3][2][0],
        totalNormalDeposited: asset[3][2][1],
        totalNormalBorrowed: asset[3][2][2],
        assetScaler: asset[3][2][3],
        assetGrowth: asset[3][2][4],
        debtGrowth: asset[3][2][5]
      },
      irmParams: {
        baseRate: asset[3][3][0],
        kinkRate: asset[3][3][1],
        slope1: asset[3][3][2],
        slope2: asset[3][3][3]
      }
    },
    riskParams: {
      riskRatio: asset[4][0],
      rangeSize: asset[4][1],
      rebalanceThreshold: asset[4][2]
    },
    sqrtAssetStatus: {
      uniswapPool: asset[5][0],
      tickLower: asset[5][1],
      tickUpper: asset[5][2],
      numRebalance: asset[5][3],
      totalAmount: asset[5][4],
      borrowedAmount: asset[5][5],
      lastRebalanceTotalSquartAmount: asset[5][6],
      lastFee0Growth: asset[5][7],
      lastFee1Growth: asset[5][8],
      borrowPremium0Growth: asset[5][9],
      borrowPremium1Growth: asset[5][10],
      fee0Growth: asset[5][11],
      fee1Growth: asset[5][12],
      rebalancePositionUnderlying: {
        positionAmount: asset[5][13][0],
        lastFeeGrowth: asset[5][13][1]
      },
      rebalancePositionStable: {
        positionAmount: asset[5][14][0],
        lastFeeGrowth: asset[5][14][1]
      },
      rebalanceFeeGrowthUnderlying: asset[5][15],
      rebalanceFeeGrowthStable: asset[5][16]
    },
    isMarginZero: asset[6],
    isIsolatedMode: asset[7],
    lastUpdateTimestamp: asset[8]
  }
}

async function queryAssetStatus51(
  contract: Controller51,
  assetId: number,
  account: string
) {
  const asset = await contract.callStatic.getLatestAssetStatus(assetId, {
    from: account
  })

  return {
    id: asset[0],
    pairGroupId: asset[1].toNumber(),
    stablePool: {
      token: asset[3][0],
      supplyTokenAddress: asset[3][1],
      tokenStatus: {
        totalCompoundDeposited: asset[3][2][0],
        totalNormalDeposited: asset[3][2][1],
        totalNormalBorrowed: asset[3][2][2],
        assetScaler: asset[3][2][3],
        assetGrowth: asset[3][2][4],
        debtGrowth: asset[3][2][5]
      },
      irmParams: {
        baseRate: asset[3][3][0],
        kinkRate: asset[3][3][1],
        slope1: asset[3][3][2],
        slope2: asset[3][3][3]
      }
    },
    underlyingPool: {
      token: asset[4][0],
      supplyTokenAddress: asset[4][1],
      tokenStatus: {
        totalCompoundDeposited: asset[4][2][0],
        totalNormalDeposited: asset[4][2][1],
        totalNormalBorrowed: asset[4][2][2],
        assetScaler: asset[4][2][3],
        assetGrowth: asset[4][2][4],
        debtGrowth: asset[4][2][5]
      },
      irmParams: {
        baseRate: asset[4][3][0],
        kinkRate: asset[4][3][1],
        slope1: asset[4][3][2],
        slope2: asset[4][3][3]
      }
    },
    riskParams: {
      riskRatio: asset[5][0],
      rangeSize: asset[5][1],
      rebalanceThreshold: asset[5][2]
    },
    sqrtAssetStatus: {
      uniswapPool: asset[6][0],
      tickLower: asset[6][1],
      tickUpper: asset[6][2],
      numRebalance: asset[6][3],
      totalAmount: asset[6][4],
      borrowedAmount: asset[6][5],
      lastRebalanceTotalSquartAmount: asset[6][6],
      lastFee0Growth: asset[6][7],
      lastFee1Growth: asset[6][8],
      borrowPremium0Growth: asset[6][9],
      borrowPremium1Growth: asset[6][10],
      fee0Growth: asset[6][11],
      fee1Growth: asset[6][12],
      rebalancePositionUnderlying: {
        positionAmount: asset[6][13][0],
        lastFeeGrowth: asset[6][13][1]
      },
      rebalancePositionStable: {
        positionAmount: asset[6][14][0],
        lastFeeGrowth: asset[6][14][1]
      },
      rebalanceFeeGrowthUnderlying: asset[6][15],
      rebalanceFeeGrowthStable: asset[6][16]
    },
    isMarginZero: asset[7],
    isIsolatedMode: asset[8],
    lastUpdateTimestamp: asset[10]
  }
}

export type AssetUtilizations = {
  stable: {
    supply: BigNumber
    borrow: BigNumber
    utilization: BigNumber
  }
  underlying: {
    supply: BigNumber
    borrow: BigNumber
    utilization: BigNumber
  }
  sqrt: {
    supply: BigNumber
    borrow: BigNumber
    utilization: BigNumber
  }
}

export function useUtilizationRatio(chainId: number, pairId: number) {
  const pair = useAsset(chainId, pairId)

  return useQuery<AssetUtilizations>(
    ['ur', chainId, pairId],

    async () => {
      if (!pair.isSuccess) throw new Error('asset not set')

      const stableTokenStatus = pair.data.stablePool.tokenStatus
      const underlyingTokenStatus = pair.data.underlyingPool.tokenStatus

      const stableSupply = stableTokenStatus.totalCompoundDeposited
        .mul(stableTokenStatus.assetScaler)
        .div(ONE)
        .add(stableTokenStatus.totalNormalDeposited)
      const stableBorrow = stableTokenStatus.totalNormalBorrowed

      const underlyingSupply = underlyingTokenStatus.totalCompoundDeposited
        .mul(underlyingTokenStatus.assetScaler)
        .div(ONE)
        .add(underlyingTokenStatus.totalNormalDeposited)
      const underlyingBorrow = underlyingTokenStatus.totalNormalBorrowed

      const sqrtAssetStatus = pair.data.sqrtAssetStatus

      return {
        stable: {
          supply: stableSupply,
          borrow: stableBorrow,
          utilization: stableSupply.eq(0)
            ? ZERO
            : stableBorrow.mul(ONE).div(stableSupply)
        },
        underlying: {
          supply: underlyingSupply,
          borrow: underlyingBorrow,
          utilization: underlyingSupply.eq(0)
            ? ZERO
            : underlyingBorrow.mul(ONE).div(underlyingSupply)
        },
        sqrt: {
          supply: sqrtAssetStatus.totalAmount,
          borrow: sqrtAssetStatus.borrowedAmount,
          utilization: sqrtAssetStatus.totalAmount.eq(0)
            ? ZERO
            : sqrtAssetStatus.borrowedAmount
                .mul(ONE)
                .div(sqrtAssetStatus.totalAmount)
        }
      }
    },

    {
      enabled: pair.isSuccess && !!chainId
    }
  )
}
